/**
 * ClearanceSummary.js
 *
 * Read-only summary view for completed clearances.
 * Displayed when clearance status is Paid, Ready for Pickup, Collected, or Cleared.
 * Shows complete information including items, payment, and collection status.
 *
 * @component
 */
import React from "react";
import {
  Descriptions,
  Tag,
  Table,
  Button,
  Space,
  Typography,
  Divider,
  Card,
  Timeline,
  Result,
} from "antd";
import {
  HomeOutlined,
  CheckCircleOutlined,
  ShoppingOutlined,
  DollarOutlined,
  InboxOutlined,
  TrophyOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

/**
 * Get status configuration (color and icon)
 */
const getStatusConfig = (status) => {
  const configs = {
    Paid: { color: "green", icon: <DollarOutlined /> },
    "Ready for Pickup": { color: "purple", icon: <InboxOutlined /> },
    "Partially Collected": { color: "geekblue", icon: <ShoppingOutlined /> },
    Collected: { color: "blue", icon: <CheckCircleOutlined /> },
    Cleared: { color: "success", icon: <TrophyOutlined /> },
  };
  return configs[status] || { color: "default", icon: null };
};

/**
 * ClearanceSummary Component
 *
 * @param {object} clearance - The clearance data
 * @param {function} onBack - Callback to return to students list
 */
const ClearanceSummary = ({ clearance, onBack }) => {
  if (!clearance) return null;

  const statusConfig = getStatusConfig(clearance.status);
  const isCleared = clearance.status === "Cleared";

  /**
   * Table columns for items
   */
  const columns = [
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
    },
    {
      title: "Brand",
      dataIndex: "brand_name",
      key: "brand_name",
    },
    {
      title: "Size",
      dataIndex: "size_name",
      key: "size_name",
      render: (text) => text || "-",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (price) => `UGX ${parseFloat(price || 0).toLocaleString()}`,
    },
    {
      title: "Total",
      dataIndex: "total_price",
      key: "total_price",
      render: (_, record) => (
        <Text strong>
          UGX{" "}
          {((record.unit_price || 0) * (record.quantity || 0)).toLocaleString()}
        </Text>
      ),
    },
  ];

  return (
    <div>
      {/* Success Banner for Cleared Status */}
      {isCleared && (
        <Result
          status="success"
          icon={<TrophyOutlined style={{ color: "#52c41a" }} />}
          title="Clearance Complete!"
          subTitle={`${clearance.student_name} has successfully completed their BOT clearance.`}
          style={{ paddingBottom: 16 }}
        />
      )}

      {/* Header for Non-Cleared Status */}
      {!isCleared && (
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>
            {statusConfig.icon}
            <span style={{ marginLeft: 8 }}>Clearance Summary</span>
          </Title>
          <Tag
            color={statusConfig.color}
            style={{ fontSize: 14, padding: "4px 12px" }}
          >
            {clearance.status}
          </Tag>
        </div>
      )}

      {/* Student & Assignment Info */}
      <Card title="Student Information" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Student Name" span={2}>
            <Text strong style={{ fontSize: 16 }}>
              {clearance.student_name}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="SPC Code">
            <Text code copyable>
              {clearance.student_spc}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="BOTPC">
            <Text code copyable>
              {clearance.botpc}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Class">
            {clearance.class_name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusConfig.color}>{clearance.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Term">
            {clearance.term_name || `Term ${clearance.term}`}
          </Descriptions.Item>
          <Descriptions.Item label="Academic Year">
            {clearance.acadyr}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Financial Summary */}
      <Card title="Payment Summary" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            flexWrap: "wrap",
            gap: 16,
            padding: 16,
            background: "#f6ffed",
            borderRadius: 8,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <Text type="secondary">Total Amount</Text>
            <div>
              <Text strong style={{ fontSize: 24 }}>
                UGX {parseFloat(clearance.total_amount || 0).toLocaleString()}
              </Text>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <Text type="secondary">Amount Paid</Text>
            <div>
              <Text strong style={{ fontSize: 24, color: "#52c41a" }}>
                UGX {parseFloat(clearance.amount_paid || 0).toLocaleString()}
              </Text>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <Text type="secondary">Balance</Text>
            <div>
              <Text
                strong
                style={{ fontSize: 24 }}
                type={parseFloat(clearance.balance) > 0 ? "danger" : "success"}
              >
                UGX {parseFloat(clearance.balance || 0).toLocaleString()}
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {/* Selected Items */}
      <Card title="Selected Items" style={{ marginBottom: 24 }}>
        <Table
          columns={columns}
          dataSource={
            clearance.selections?.map((sel, index) => ({
              key: index,
              ...sel,
            })) || []
          }
          pagination={false}
          scroll={{ x: 600 }}
          size="small"
        />
      </Card>

      {/* Progress Timeline */}
      <Card title="Clearance Progress" style={{ marginBottom: 24 }}>
        <Timeline
          mode="left"
          items={[
            {
              color: "green",
              children: (
                <div>
                  <Text strong>Items Selected</Text>
                  <br />
                  <Text type="secondary">
                    {clearance.selections?.length || 0} items selected
                  </Text>
                </div>
              ),
            },
            {
              color: "green",
              children: (
                <div>
                  <Text strong>Selections Confirmed</Text>
                  <br />
                  <Text type="secondary">Items locked for payment</Text>
                </div>
              ),
            },
            {
              color: "green",
              children: (
                <div>
                  <Text strong>Payment Complete</Text>
                  <br />
                  <Text type="secondary">
                    UGX{" "}
                    {parseFloat(clearance.amount_paid || 0).toLocaleString()}{" "}
                    paid
                  </Text>
                </div>
              ),
            },
            {
              color: [
                "Ready for Pickup",
                "Partially Collected",
                "Collected",
                "Cleared",
              ].includes(clearance.status)
                ? "green"
                : "gray",
              children: (
                <div>
                  <Text strong>Items Prepared</Text>
                  <br />
                  <Text type="secondary">
                    {[
                      "Ready for Pickup",
                      "Partially Collected",
                      "Collected",
                      "Cleared",
                    ].includes(clearance.status)
                      ? "Ready for collection at warehouse"
                      : "Pending preparation"}
                  </Text>
                </div>
              ),
            },
            {
              color: ["Collected", "Cleared"].includes(clearance.status)
                ? "green"
                : "gray",
              children: (
                <div>
                  <Text strong>Items Collected</Text>
                  <br />
                  <Text type="secondary">
                    {["Collected", "Cleared"].includes(clearance.status)
                      ? "All items collected"
                      : clearance.status === "Partially Collected"
                        ? "Some items collected"
                        : "Pending collection"}
                  </Text>
                </div>
              ),
            },
            {
              color: isCleared ? "green" : "gray",
              children: (
                <div>
                  <Text strong>Clearance Complete</Text>
                  <br />
                  <Text type="secondary">
                    {isCleared ? "All done!" : "Pending verification"}
                  </Text>
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Next Steps Alert (if not cleared) */}
      {!isCleared && (
        <Card style={{ marginBottom: 24 }}>
          <Title level={5}>Next Steps</Title>
          {clearance.status === "Paid" && (
            <Text>
              Your items are being prepared. Please wait for notification when
              they are ready for pickup.
            </Text>
          )}
          {clearance.status === "Ready for Pickup" && (
            <Text>
              Your items are ready! Please visit the warehouse to collect them.
            </Text>
          )}
          {clearance.status === "Partially Collected" && (
            <Text>
              Some items are pending collection. Please visit the warehouse to
              collect remaining items.
            </Text>
          )}
          {clearance.status === "Collected" && (
            <Text>All items collected. Waiting for final verification.</Text>
          )}
        </Card>
      )}

      {/* Back Button */}
      <div style={{ textAlign: "center" }}>
        <Button
          type="primary"
          size="large"
          icon={<HomeOutlined />}
          onClick={onBack}
        >
          Back to Students
        </Button>
      </div>
    </div>
  );
};

export default ClearanceSummary;
